import { transition } from '@pixelunion/animations';
import EventHandler from '@pixelunion/events';

export default class FacetedFilters {
  constructor(section) {
    this.el = section.el;

    this.events = new EventHandler();

    this.filterInputs = null;
    this.dropdowns = this.el.querySelectorAll('[data-filter-group]');
    this.summaryEls = this.el.querySelectorAll('[data-filter-group-summary]');
    this.filterContents = this.el.querySelectorAll('[data-filter-group-content]');

    this.closedState = 'closed';
    this.openState = 'open';
    this.disclosureAnimations = {};

    this.filterRange = this.el.querySelectorAll('[data-filter-range]');
    this.rangeNames = [];

    this.filterRange.forEach(el => {
      this.rangeNames.push(el.name);
    });

    this.updateTimeout = null;

    this.initFilterEvents = this.initFilterEvents.bind(this);
    this.updatePrice = this.updatePrice.bind(this);
    this.buildFilterQuery = this.buildFilterQuery.bind(this);

    this.initAnimations();
    this.initFilterEvents();
  }

  initFilterEvents() {
    /* For radio buttons */
    this.filterInputs = this.el.querySelectorAll('[data-filter-input]');

    this.filterInputs.forEach(filter => {
      this.events.register(filter, 'click', e => {
        const target = e.currentTarget;
        if (target.dataset.hasOwnProperty('disabled')) {
          e.preventDefault();
          return;
        }

        this._filterClick(target);
      });
    });

    this.filterContents.forEach(content => {
      this.events.register(window, 'resize', () => this._setStyleVariables(content));
    });

    this.events.register(window, 'keyup', e => {
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
      }
    });

    /* For range input */
    this.filterRange.forEach(filter => {
      this.events.register(filter, 'change', e => this.updatePrice(e.currentTarget));
      this.events.register(filter, 'keyup', e => {
        if (e.key !== 'Tab') {
          this.updatePrice(e.currentTarget);
        }
      });
    });

    this.summaryEls.forEach(summary => {
      this.events.register(summary, 'click', event => {
        this.animateDisclosure(event);
      });
    });

    /* When clicking outside dropdown element, close dropdown */
    this.events.register(document.body, 'click', e => {
      if (!e.target.closest('[data-filter-group]')) {
        this.closeAllDropdowns();
      }
    });
  }

  _filterClick(target) {
    const checkbox = target.querySelector('[data-filter-checkbox]');

    if (target.hasAttribute('data-filter-active')) {
      checkbox.removeAttribute('data-checked');
      target.removeAttribute('data-filter-active');
    } else {
      checkbox.setAttribute('data-checked', '');
      target.setAttribute('data-filter-active', '');
    }
  }

  /*
   * Initialize animations on checkbox container
   * using filter tag as JSON key
   */

  initAnimations() {
    this.summaryEls.forEach(summary => {
      const filterList = summary.nextElementSibling;
      const currentDetailsEl = summary.closest('[data-filter-group]');
      const index = summary.dataset.filterGroupIndex;
      this.disclosureAnimations[index] = transition({ el: filterList, state: this.closedState });
      currentDetailsEl.style.setProperty('--closed-height', `${summary.offsetHeight}px`);
    });
  }

  buildFilterQuery(element) {
    let searchParameters;

    // If input is price range, the url_to_add property is not available, so we
    // need to build the query manually
    if (element.classList.contains('filter-range-input')) {
      const currentSearch = window.location.search.replace('?', '');
      const formData = new FormData(element.closest('form'));
      const newSearch = new URLSearchParams(formData).toString();

      // Filter through current search string to see if price range input already exists,
      // if it does, create new string without current price range parameters
      const splitCurrentSearch = currentSearch.split('&');

      let filteredCurrentSearch = splitCurrentSearch.filter(search => {
        if (search.includes(this.rangeNames[0]) || search.includes(this.rangeNames[1])) {
          return false;
        }

        return true;
      });

      filteredCurrentSearch = filteredCurrentSearch.join('&');

      // If current search parameters exist, append new search parameters
      // on to existing parameters
      if (filteredCurrentSearch.length > 0) {
        searchParameters = `${filteredCurrentSearch}&${newSearch}`;
      } else {
        searchParameters = `${newSearch}`;
      }
    }

    let urlQueryString = '';

    urlQueryString = `?${searchParameters}`;

    return urlQueryString;
  }

  updatePrice(element) {
    // cancel any pending requests
    if (this.updateTimeout !== null) {
      clearTimeout(this.updateTimeout);
    }

    // Wait for user to finish entering input before reloading page
    this.updateTimeout = setTimeout(() => {
      this.getFilteredResults(element);
    }, 2000);
  }

  getFilteredResults(element) {
    const filterQuery = this.buildFilterQuery(element);
    window.location.search = filterQuery;
  }

  animateDisclosure(event) {
    event.preventDefault();
    const summary = event.currentTarget;
    const filterList = summary.nextElementSibling;
    const currentDetailsEl = summary.closest('[data-filter-group]');

    /* Handle dropdowns on click event outside disclosure */
    if (filterList == null) {
      this.closeAllDropdowns();
    } else {
      if (filterList.dataset.animation) return;

      if (filterList.dataset.animationState === this.openState) {
        this.closeDisclosure(currentDetailsEl);
      } else {
        /* Close any open dropdowns before opening new dropdown */
        this.closeAllDropdowns();
        this.openDisclosure(currentDetailsEl);
      }
    }
  }

  _setStyleVariables(el) {
    // On desktop, we should check if the dropdown overflows the
    // viewport, and add a class if it does so we can align it
    // relative to the summary element accordingly

    const { right: rightBounds, left: leftBounds } = el.getBoundingClientRect();

    if (rightBounds >= window.innerWidth) {
      el.classList.add('filters--overflow-right');
    }

    if (leftBounds <= 0) {
      el.classList.add('filters--overflow-left');
    }

    // We need to absolutely position the open disclosure to center it on mobile
    // and make it full screen, so we set some CSS variables to do that
    const wrapper = el.closest('.faceted-filters');

    const { width: mobileWidth, left: mobileLeft } = wrapper.getBoundingClientRect();

    el.style.setProperty('--mobile-width', `${mobileWidth}px`);
    el.style.setProperty('--mobile-left', `${mobileLeft}px`);

    el.style.setProperty('--open-height', `${el.offsetHeight}px`);
  }

  openDisclosure(detailsEl) {
    const index = detailsEl.querySelector('[data-filter-group-summary]').dataset.filterGroupIndex;
    detailsEl.setAttribute('open', '');

    this.disclosureAnimations[index]
      .animateTo(this.openState, {
        onStart: ({ el }) => {
          this._setStyleVariables(el);
        }
      });

    detailsEl.classList.add('dropdown-expanded');
  }

  closeDisclosure(detailsEl) {
    const index = detailsEl.querySelector('[data-filter-group-summary]').dataset.filterGroupIndex;

    this.disclosureAnimations[index]
      .animateTo(this.closedState)
      .then(() => {
        detailsEl.removeAttribute('open');
      });

    detailsEl.classList.remove('dropdown-expanded');
  }

  closeAllDropdowns() {
    this.dropdowns.forEach(dropdown => {
      if (dropdown.classList.contains('dropdown-expanded')) {
        this.closeDisclosure(dropdown);
      }
    });
  }

  unload() {
    if (this.events) {
      this.events.unregisterAll();
    }
  }
}
